import { ChatMessage } from '../../../services/chatSupportService';
import { ChatMessageResponse } from '../../../services/chatSupportService';

/**
 * Generate a consistent message ID for user messages
 * 
 * @param sessionId - The session ID
 * @param timestamp - Optional timestamp, defaults to current time
 * @returns Consistent message ID
 */
export const generateUserMessageId = (sessionId: string, timestamp?: number): string => {
  const time = timestamp || Date.now();
  return `user_${sessionId}_${time}`;
};

/**
 * Generate a consistent message ID for support messages
 * 
 * @param sessionId - The session ID
 * @param timestamp - Optional timestamp, defaults to current time
 * @returns Consistent message ID
 */
export const generateSupportMessageId = (sessionId: string, timestamp?: number): string => {
  const time = timestamp || Date.now();
  return `support_${sessionId}_${time}`;
};

/**
 * Convert API message response to ChatMessage format
 * 
 * @param apiMessage - Message from API response
 * @param sessionId - The session ID
 * @returns ChatMessage object
 */
export const convertApiMessageToChatMessage = (
  apiMessage: ChatMessageResponse,
  sessionId: string
): ChatMessage => {
  // Map from_user to sender
  let sender: 'user' | 'support' = 'user';
  if (apiMessage.from_user === 'agent') {
    sender = 'support';
  } else if (apiMessage.from_user === 'customer') {
    sender = 'user';
  } else if (apiMessage.from_user === 'system') {
    sender = 'support'; // System messages appear as support messages
  }

  // Use API message_id if available, otherwise generate one
  const messageId = apiMessage.message_id || 
    (sender === 'user' ? generateUserMessageId(sessionId) : generateSupportMessageId(sessionId));

  return {
    id: messageId,
    sessionId: sessionId,
    message: formatMessageText(apiMessage.message), // Apply formatting to API messages
    sender: sender,
    timestamp: apiMessage.timestamp,
    isRead: true
  };
};

/**
 * Convert array of API messages to ChatMessage array
 * 
 * @param apiMessages - Array of messages from API response
 * @param sessionId - The session ID
 * @returns Array of ChatMessage objects sorted by timestamp (latest at the end)
 */
export const convertApiMessagesToChatMessages = (
  apiMessages: ChatMessageResponse[],
  sessionId: string
): ChatMessage[] => {
  const filteredMessages = apiMessages.filter((_, index) => index !== 0);

  const chatMessages = filteredMessages.map((message) =>
    convertApiMessageToChatMessage(message, sessionId)
  );
  
  // Sort messages by timestamp with latest messages at the end
  return chatMessages.sort((a, b) => {
    const timestampA = new Date(a.timestamp).getTime();
    const timestampB = new Date(b.timestamp).getTime();
    return timestampA - timestampB;
  });
};

/**
 * Format message text to properly handle escape characters
 * 
 * @param messageText - Raw message text from API
 * @returns Formatted message text with proper line breaks and indentation
 */
export const formatMessageText = (messageText: string): string => {
  if (!messageText) return '';
  
  // Replace escape characters with proper formatting
  return messageText
    .replace(/\\n/g, '\n')  // Replace \n with actual newlines
    .replace(/\\t/g, '\t')  // Replace \t with actual tabs
    .replace(/\\r/g, '\r')  // Replace \r with actual carriage returns
    .replace(/\\\\/g, '\\') // Replace \\ with single backslash
    .trim(); // Remove leading/trailing whitespace
};

/**
 * Split formatted text into lines for proper rendering
 * 
 * @param text - Formatted text with line breaks
 * @returns Array of text lines
 */
export const splitMessageIntoLines = (text: string): string[] => {
  if (!text) return [];
  
  const formattedText = formatMessageText(text);
  
  // Split by newlines and process each line
  return formattedText.split('\n').map(line => {
    // Handle tabs by converting them to spaces
    return line.replace(/\t/g, '    '); // Convert tabs to 4 spaces
  });
};

/**
 * Sort an array of ChatMessage objects by timestamp
 * 
 * @param messages - Array of ChatMessage objects
 * @returns Array of ChatMessage objects sorted by timestamp (latest at the end)
 */
export const sortMessagesByTimestamp = (messages: ChatMessage[]): ChatMessage[] => {
  return messages.sort((a, b) => {
    const timestampA = new Date(a.timestamp).getTime();
    const timestampB = new Date(b.timestamp).getTime();
    return timestampA - timestampB;
  });
};
